import { Motif, MotifResponse, PhotoResponse } from '../types/motifs'
import { Tag, tags, keywordTags } from '../constants/tags'
import { Region, regions } from '../constants/geographicFilters'

export function mapMotifResponse(res: MotifResponse[]): Motif[] {
  return res.map(r => {
    const svgUrl = getPhotoUrl(r?.SvgObjectPhoto, true)

    return {
      id: r.id,
      title: r.Title,
      EN_title: r.EN_Title,
      subtitle: r.SubTitle,
      EN_subtitle: r.EN_SubTitle,
      description: r.Description,
      EN_description: r.EN_Description,
      AdditionalPhotos: r.AdditionalPhotos?.map(photo => getPhotoUrl(photo)) ?? undefined,
      photoUrl: getPhotoUrl(r?.OriginalObjectPhoto),
      originalSVGUrl: svgUrl || null,
      vectorizedUrl: getPhotoUrl(r?.VectorizedObjectPhoto, true),
      vectorizedUrlXs: r?.VectorizedObjectPhoto?.formats?.thumbnail?.url
        ? `${process.env.REACT_APP_BACKEND_URL}${r.VectorizedObjectPhoto.formats.thumbnail.url}`
        : null,
      year: r.ExactDate || r.TimeRange || r.Century || null,
      EN_year: r.EN_ExactDate || r.EN_TimeRange || r.EN_Century || null,
      tagIds: r?.TagList?.map(tag => Number(tag.id)) ?? [],
      country: r.Country ?? null,
      EN_country: r.EN_Country ?? null,
      region: r.Region ?? null,
      EN_region: r.EN_Region ?? null,
      settlement: r.Settlement ?? null,
      EN_settlement: r.EN_Settlement ?? null,
      inventoryNumber: r.InventoryNumber,
    }
  })
}

function getPhotoUrl(res: PhotoResponse | undefined, preferMedium = false): string {
  const path = preferMedium
    ? res?.formats?.medium?.url ??
      res?.formats?.large?.url ??
      res?.url ??
      res?.formats?.small?.url ??
      ''
    : res?.formats?.large?.url ??
      res?.url ??
      res?.formats?.medium?.url ??
      res?.formats?.small?.url ??
      ''

  if (!path) return ''
  return process.env.REACT_APP_BACKEND_URL + path
}

export function filterByTag(motifs: Motif[], tag: Tag): Motif[] {
  const filterTagIds = tags[tag]
  return motifs.filter(motif => motif.tagIds.some(tagId => filterTagIds.includes(tagId)))
}

export function filterByRegion(motifs: Motif[], region: Region): Motif[] {
  const filterRegionIds = regions[region]
  return motifs.filter(motif => motif.tagIds.some(tagId => filterRegionIds.includes(tagId)))
}

export function filterByBlacklist(motifs: Motif[]): Motif[] {
  const blacklistTags = process.env.REACT_APP_BLACKLIST_FILTER_TAGS || ''
  if (!blacklistTags) return motifs

  const tagsArray = blacklistTags.split(',').map(tag => tag.trim() as Tag)
  const tagIdsToExclude = tagsArray.flatMap(tag => tags[tag])

  return motifs.filter(motif => !motif.tagIds.some(tagId => tagIdsToExclude.includes(tagId)))
}

export function filterByWhitelist(motifs: Motif[]): Motif[] {
  const whitelistTags = process.env.REACT_APP_WHITELIST_FILTER_TAGS || ''
  if (!whitelistTags) return motifs

  const tagsArray = whitelistTags.split(',').map(tag => tag.trim() as Tag)
  const tagIdsToInclude = tagsArray.flatMap(tag => tags[tag])

  return motifs.filter(motif => motif.tagIds.some(tagId => tagIdsToInclude.includes(tagId)))
}

export function filterByKeyword(motifs: Motif[], keywordId: number): Motif[] {
  const keyword = keywordTags.find(tag => tag.id === keywordId)
  if (!keyword) return motifs

  const keywordItems = new Set(keyword.items)

  return motifs.filter(
    motif => keywordItems.has(motif.id) || motif.tagIds.some(tagId => keywordItems.has(tagId)),
  )
}
